<script setup lang="ts">
import CloseBoldIcon from '~/assets/icon/close-2.svg'
import ServiceIcon from '~/assets/icon/service.svg'

const { t, locale } = useAppI18n()
const isChineseLocale = locale.toLowerCase().startsWith('zh')
const { show, traderData } = defineProps<{
  show: boolean
  traderData?: any
}>()
const traderName = computed(() => traderData?.basicInfo?.shortName || '--')
const mediationNote = computed(() => t('83797', [traderName.value]))

const emc = computed(() => traderData?.emc || null)

const emit = defineEmits<{
  close: []
  feedback: []
}>()

const dialogMask = useTemplateRef<HTMLElement>('dialogMask')
let previousBodyOverflow = ''
let previousHtmlOverflow = ''

const close = () => {
  emit('close')
}

const feedback = () => {
  emit('feedback')
}

watch(
  () => show,
  async (value) => {
    if (!import.meta.client) return

    if (value) {
      previousBodyOverflow = document.body.style.overflow
      previousHtmlOverflow = document.documentElement.style.overflow
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
      await nextTick()
      dialogMask.value?.focus()
      return
    }

    document.body.style.overflow = previousBodyOverflow
    document.documentElement.style.overflow = previousHtmlOverflow
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  if (import.meta.client) {
    document.body.style.overflow = previousBodyOverflow
    document.documentElement.style.overflow = previousHtmlOverflow
  }
})
</script>

<template>
  <Teleport to="body">
    <Transition name="emc-dialog-fade">
      <section v-if="show" ref="dialogMask" class="emc-dialog-mask" tabindex="-1" @keydown.esc="close">
        <div class="emc-dialog" role="dialog" aria-modal="true" aria-labelledby="emc-dialog-title">
          <header class="emc-dialog-header">
            <div class="emc-dialog-title-row">
              <h2 id="emc-dialog-title" class="emc-dialog-title">
                {{ t('83788') }}
              </h2>
              <button type="button" class="emc-dialog-close" aria-label="关闭 EMC 客服直通弹窗" @click="close">
                <CloseBoldIcon />
              </button>
            </div>
          </header>

          <div class="emc-dialog-body">
            <div class="emc-dialog-content">
              <div class="emc-dialog-highlights" :class="{ 'is-single-col': !isChineseLocale }">
                <article class="emc-dialog-highlight">
                  <svg class="emc-dialog-highlight-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" fill="currentColor" />
                    <path d="M12 6.5V12H16" stroke="white" stroke-width="1.8" stroke-linecap="round" />
                  </svg>
                  <div class="emc-dialog-highlight-copy">
                    <h3>{{ t('83789') }}</h3>
                    <p>{{ t('83790') }}</p>
                  </div>
                </article>

                <article class="emc-dialog-highlight">
                  <ServiceIcon class="emc-dialog-highlight-icon" aria-hidden="true" />
                  <div class="emc-dialog-highlight-copy">
                    <h3>{{ t('83791') }}</h3>
                    <p>{{ t('83792') }}</p>
                  </div>
                </article>
              </div>

              <section
                class="emc-dialog-progress"
                aria-labelledby="emc-dialog-progress-title"
                v-if="emc?.mediation?.isShow"
              >
                <h3 id="emc-dialog-progress-title">{{ t('83793') }}</h3>
                <div class="emc-dialog-progress-list">
                  <div class="emc-dialog-progress-item">
                    <span>{{ t('83794') }}</span>
                    <strong>{{ emc?.mediation?.allCount || '--' }}</strong>
                  </div>
                  <div class="emc-dialog-progress-divider" aria-hidden="true"></div>
                  <div class="emc-dialog-progress-item">
                    <span>{{ t('83795') }}</span>
                    <strong>{{ emc?.mediation?.doneCount || '--' }}</strong>
                  </div>
                  <div class="emc-dialog-progress-divider" aria-hidden="true"></div>
                  <div class="emc-dialog-progress-item">
                    <span>{{ t('83796') }}</span>
                    <strong>{{ emc?.mediation?.handlingCount || '--' }}</strong>
                  </div>
                </div>
              </section>

              <div class="emc-dialog-note">
                <p>{{ mediationNote }}</p>
                <p>{{ t('83798') }}</p>
              </div>
            </div>

            <button type="button" class="emc-dialog-feedback" @click="feedback">
              {{ t('83799') }}
            </button>
          </div>
        </div>
      </section>
    </Transition>
  </Teleport>
</template>

<style scoped lang="less">
.emc-dialog-mask {
  position: fixed;
  z-index: 3000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(9, 20, 51, 0.48);
  padding: 32px;
  outline: none;
}

.emc-dialog {
  display: flex;
  width: min(540px, calc(100vw - 48px));
  max-height: calc(100vh - 64px);
  flex-direction: column;
  overflow: hidden;
  border-radius: 28px;
  background: #fff;
  box-shadow: 0 24px 80px rgba(8, 19, 62, 0.18);
}

.emc-dialog-header {
  background: #fff;
  padding: 32px 32px 0;
}

.emc-dialog-title-row {
  display: flex;
  min-height: 48px;
  align-items: center;
  gap: var(--spacing-m, 8px);
  border-bottom: 0.5px solid var(--color-border-default, rgba(5, 21, 36, 0.06));
  padding-bottom: 16px;
}

.emc-dialog-title {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  margin: 0;
  color: var(--color-text, #292a2e);
  font-family:
    PingFang SC,
    Outfit,
    sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-break: break-word;
}

.emc-dialog-close {
  display: flex;
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: transparent;
  padding: 0;
  transition: transform 0.2s ease;
}

.emc-dialog-close:focus-visible {
  outline: 2px solid rgba(41, 42, 46, 0.32);
  outline-offset: 2px;
}

.emc-dialog-close :deep(svg) {
  display: block;
  width: 32px;
  height: 32px;
}

.emc-dialog-body {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  overflow-y: auto;
  background: #fff;
  padding: 20px 32px 32px;
}

.emc-dialog-content {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 16px;
}

.emc-dialog-highlights {
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  gap: 16px;
}

.emc-dialog-highlights.is-single-col .emc-dialog-highlight {
  flex: 1 0 100%;
}

.emc-dialog-highlight {
  display: flex;
  flex: 1 0 calc(50% - 8px);
  min-width: min(100%, max-content);
  max-width: 100%;
  align-items: center;
  gap: 12px;
  border: 0.5px solid var(--color-border-default, rgba(5, 21, 36, 0.06));
  border-radius: 8px;
  padding: 12px;
}

.emc-dialog-highlight-icon {
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
  color: var(--color-icon-default, #292a2e);
}

.emc-dialog-highlight-copy {
  min-width: 0;
  flex: 1;
}

.emc-dialog-highlight-copy h3,
.emc-dialog-highlight-copy p,
.emc-dialog-progress h3,
.emc-dialog-note p {
  margin: 0;
}

.emc-dialog-highlight-copy h3,
.emc-dialog-progress h3 {
  color: var(--color-text, #292a2e);
  font-family:
    Outfit,
    PingFang SC,
    sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
}

.emc-dialog-highlight-copy p,
.emc-dialog-progress-item span,
.emc-dialog-note {
  color: var(--color-text-subtlest, #7d818a);
  font-family:
    Outfit,
    PingFang SC,
    sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.emc-dialog-progress {
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  gap: 12px;
  border: 0.5px solid var(--color-border-default, rgba(5, 21, 36, 0.06));
  border-radius: 8px;
  padding: 12px;
}

.emc-dialog-progress h3 {
  text-align: center;
}

.emc-dialog-progress-list {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;
}

.emc-dialog-progress-item {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-align: center;
}

.emc-dialog-progress-item strong {
  width: 100%;
  overflow: hidden;
  color: var(--color-text, #292a2e);
  font-family:
    Outfit,
    PingFang SC,
    sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.emc-dialog-progress-divider {
  width: 0.5px;
  height: 32px;
  flex: 0 0 auto;
  background: var(--color-border-default, rgba(5, 21, 36, 0.06));
}

.emc-dialog-note {
  width: 100%;
  border-radius: 8px;
  background: var(--color-background-subtlest, #f8f8f8);
  padding: 12px;
}

.emc-dialog-feedback {
  display: flex;
  width: 184px;
  min-height: 46px;
  flex: 0 0 auto;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 100px;
  background: var(--color-brand-default, #112d73);
  color: #fff;
  font-family:
    Outfit,
    PingFang SC,
    sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  padding: 12px 64px;
  white-space: nowrap;
  transition:
    background 0.2s ease,
    transform 0.2s ease;
}

.emc-dialog-feedback:hover {
  background: #173c91;
}

.emc-dialog-feedback:focus-visible {
  outline: 2px solid rgba(17, 45, 115, 0.32);
  outline-offset: 2px;
}

.emc-dialog-fade-enter-active,
.emc-dialog-fade-leave-active {
  transition: opacity 0.2s ease;
}

.emc-dialog-fade-enter-active .emc-dialog,
.emc-dialog-fade-leave-active .emc-dialog {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.emc-dialog-fade-enter-from,
.emc-dialog-fade-leave-to {
  opacity: 0;
}

.emc-dialog-fade-enter-from .emc-dialog,
.emc-dialog-fade-leave-to .emc-dialog {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}

@media screen and (max-width: 799px) {
  .emc-dialog-mask {
    padding: 16px;
  }

  .emc-dialog {
    height: auto;
    width: 100%;
    border-radius: 20px;
  }

  .emc-dialog-header {
    padding: 24px 24px 0;
  }

  .emc-dialog-body {
    padding: 20px 24px 24px;
  }

  .emc-dialog-highlights {
    flex-direction: column;
  }
}
</style>
